import { formatCurrency } from './currency';
import type { PZDocument } from '../types/pz';

type PZTotalLine = {
  currency?: string;
  quantityOrdered?: number;
  purchaseQuantity?: number | null;
  unitPrice?: number;
  totalPrice?: number;
  exchangeRatePLN?: number;
  totalPricePLN?: number;
};

export interface PZDocumentTotal {
  amount: number;
  currency: string;
  mixedCurrency: boolean;
}

const toFiniteNumber = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getLineRawTotal = (item: PZTotalLine): number => {
  const totalPrice = toFiniteNumber(item.totalPrice);
  if (totalPrice !== null) return totalPrice;

  const pricingQuantity = toFiniteNumber(item.purchaseQuantity) ?? toFiniteNumber(item.quantityOrdered) ?? 0;
  return pricingQuantity * (toFiniteNumber(item.unitPrice) ?? 0);
};

const getLinePLNTotal = (item: PZTotalLine): number => {
  const totalPricePLN = toFiniteNumber(item.totalPricePLN);
  if (totalPricePLN !== null) return totalPricePLN;

  const rawTotal = getLineRawTotal(item);
  const currency = item.currency || 'PLN';
  if (currency === 'PLN') return rawTotal;

  const exchangeRatePLN = toFiniteNumber(item.exchangeRatePLN);
  return exchangeRatePLN !== null ? rawTotal * exchangeRatePLN : 0;
};

export const calculatePZDocumentTotal = (
  items: PZTotalLine[] = [],
  documentCurrency = 'PLN',
): PZDocumentTotal => {
  const currencies = new Set(items.map((item) => item.currency || documentCurrency || 'PLN'));
  const mixedCurrency = currencies.size > 1;

  if (mixedCurrency) {
    return {
      amount: items.reduce((sum, item) => sum + getLinePLNTotal(item), 0),
      currency: 'PLN',
      mixedCurrency: true,
    };
  }

  return {
    amount: items.reduce((sum, item) => sum + getLineRawTotal(item), 0),
    currency: Array.from(currencies)[0] || documentCurrency || 'PLN',
    mixedCurrency: false,
  };
};

export const formatPZDocumentTotal = (document: Pick<PZDocument, 'items' | 'currency' | 'totalValue'>): string => {
  if (document.items?.length) {
    const total = calculatePZDocumentTotal(document.items, document.currency || 'PLN');
    return formatCurrency(total.amount, total.currency);
  }

  const totalValue = toFiniteNumber(document.totalValue);
  return totalValue ? formatCurrency(totalValue, document.currency || 'PLN') : '-';
};
